/**
 * StressTwinPage — Cortisol Intelligence, подредена като KHI/BHI:
 * герой с оценка, табове най-отгоре, после интелигентността и програмата.
 */
import React, { useEffect, useState } from "react";
import PhysiologyBridgeCard from "@/components/PhysiologyBridgeCard";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { MobileTopBar } from "@/components/MobileTopBar";
import {
  Loader2, ArrowLeft, Zap, Save, Activity, Brain, CalendarCheck, Sparkles,
  TrendingDown, Utensils,
} from "lucide-react";
import { toast } from "sonner";
import { twinLabel, moduleTitle } from "@/lib/twinCatalog";
import CortisolThyroidAxisCard from "@/components/dashboard/CortisolThyroidAxisCard";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";
import DailyCheckinCard from "@/components/shared/DailyCheckinCard";  // iter1054
import BreastPainBrief from "@/components/breast/BreastPainBrief";    // iter1059
import CortisolIntelCard from "@/components/stress/CortisolIntelCard";
import CortisolProgramCard from "@/components/stress/CortisolProgramCard";
import CortisolTrendCard from "@/components/stress/CortisolTrendCard";
import CortisolPriorityCard from "@/components/stress/CortisolPriorityCard";
import RelaxationPlateCard from "@/components/stress/RelaxationPlateCard";

const TABS = [
  { id: "today", label: "Днес", icon: Activity },
  { id: "intel", label: moduleTitle("cortisol"), icon: Brain },
  { id: "program", label: "28-дневна програма", icon: CalendarCheck },
];

const BAND_COLOR = (load) =>
  load == null ? "#94a3b8"
  : load < 30 ? "#10B981"
  : load < 55 ? "#F59E0B"
  : load < 75 ? "#F97316"
  : "#EF4444";

function ScoreRing({ value, color }) {
  const r = 52, c = 2 * Math.PI * r;
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value)) / 100;
  return (
    <svg width="128" height="128" viewBox="0 0 128 128" data-testid="stress-score-ring">
      <circle cx="64" cy="64" r={r} fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth="10" />
      <circle cx="64" cy="64" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform="rotate(-90 64 64)"
        style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      <text x="64" y="60" textAnchor="middle" fontSize="30" fontWeight="800" fill="currentColor">
        {value ?? "—"}
      </text>
      <text x="64" y="80" textAnchor="middle" fontSize="12" fill="currentColor" fillOpacity="0.55">/ 100</text>
    </svg>
  );
}

function Section({ icon: Icon, title, help, children, testid }) {
  return (
    <section className="mb-6 scroll-mt-20" id={testid} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-5 h-5 text-orange-500" />
        <h2 className="text-base md:text-lg font-bold">{title}</h2>
      </div>
      {help && <p className="text-xs opacity-60 mb-3">{help}</p>}
      {children}
    </section>
  );
}

export default function StressTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [insight, setInsight] = useState("");
  const [loading, setLoading] = useState(true);
  const [perceived, setPerceived] = useState(5);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [axis, setAxis] = useState(null);
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("stress-tab") || "today"; } catch { return "today"; }
  });
  useEffect(() => {
    try { localStorage.setItem("stress-tab", tab); } catch { /* ignore */ }
  }, [tab]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/stress-twin/insights`, {}, { timeout: 30000 });
      setSnap(r.data.snapshot);
      setInsight(r.data.insight_bg || "");
    } catch {
      try {
        const { data } = await axios.get(`${API}/stress-twin/snapshot`);
        setSnap(data);
      } catch { /* ignore */ }
    }
    try {
      const { data } = await axios.get(`${API}/stress-twin/cortisol-thyroid-axis`);
      setAxis(data?.detected ? data : null);
    } catch {
      setAxis(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    const handler = () => fetchAll();
    window.addEventListener("thyroid-lab-changed", handler);
    window.addEventListener("stress-log-changed", handler);
    window.addEventListener("nutramilo:meals:updated", handler);
    return () => {
      window.removeEventListener("thyroid-lab-changed", handler);
      window.removeEventListener("stress-log-changed", handler);
      window.removeEventListener("nutramilo:meals:updated", handler);
    };
    /* eslint-disable-next-line */
  }, [API]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/stress-twin/log`, {
        perceived_1_10: parseInt(perceived, 10), note_bg: note || null,
      });
      toast.success("Записано");
      setNote("");
      fetchAll();
      window.dispatchEvent(new Event("stress-log-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    }
    setSaving(false);
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const load = snap?.composite;
  const color = BAND_COLOR(load);

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}
      data-testid="stress-twin-page">
      <MobileTopBar title="Напрежение" fallbackTo="/dashboard" testid="stress-twin-mobile-topbar" />
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <button onClick={() => navigate("/dashboard")}
          className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1"
          data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        {/* Герой */}
        <div className="flex items-start gap-3 mb-2">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
            isDark ? "bg-orange-500/20" : "bg-orange-100"}`}>
            <Zap className={`w-6 h-6 ${isDark ? "text-orange-300" : "text-orange-600"}`} />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight bg-gradient-to-r
                           from-orange-500 via-amber-500 to-rose-500 bg-clip-text text-transparent">
              {twinLabel("stress")}
            </h1>
            <p className="text-sm opacity-70 mt-1">
              Стрес и кортизол на прост език — какво показва денят Ви, какъв е профилът Ви
              и какво да правите в следващите 28 дни.
            </p>
          </div>
        </div>

        {/* Табове най-отгоре — както в KHI */}
        <div className="flex gap-1.5 overflow-x-auto pb-2 my-3 -mx-1 px-1" data-testid="stress-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`shrink-0 px-3.5 py-2 rounded-xl text-xs font-bold transition-all
                inline-flex items-center gap-1.5 ${
                tab === t.id
                  ? "bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-md"
                  : isDark ? "bg-white/5 hover:bg-white/10 text-slate-300"
                           : "bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"}`}
              data-testid={`stress-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        <ProfileContextStrip domain="stress" isDark={isDark} className="mb-4" />

        {/* iter1054 — един вход за три близнака */}
        <div className="mb-4">
          <DailyCheckinCard isDark={isDark} />
        </div>

        {/* iter1046 — кортизоловият ритъм показва формата; двойката кортизол + АКТХ казва етажа */}
        <button
          onClick={() => navigate("/dashboard/hormonal-twin")}
          data-testid="stress-to-endocrine-axes"
          className="mb-4 w-full text-left text-[11px] px-3 py-2 rounded-lg bg-violet-500/10 hover:bg-violet-500/20 transition-colors"
        >
          <strong>Ендокринни оси — къде е повредата →</strong> Кривата показва
          формата на кортизола. Двойката кортизол сутрин + АКТХ казва дали
          проблемът е в надбъбрека или в хипофизата.
        </button>

        {loading ? (
          <Loader2 className="w-7 h-7 animate-spin text-orange-500 mx-auto my-16" />
        ) : (
          <>
            {tab === "today" && (
              <>
                {/* iter1059 — болката в гърдата се чете и през кортизола */}
                <BreastPainBrief isDark={isDark} context="cortisol" />

                <CortisolPriorityCard isDark={isDark} onGoTo={(t) => setTab(t)} />

                <Section icon={Activity} title="Кортизолов товар за 7 дни"
                  help="Съставна оценка от храненето, съня и усещането Ви за стрес."
                  testid="stress-section-load">
                  <div className={`p-5 rounded-2xl border ${cardCls}`} data-testid="stress-card">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <ScoreRing value={load} color={color} />
                      <div className="min-w-0 flex-1">
                        {snap?.band && (
                          <div className="text-sm font-bold" style={{ color }} data-testid="stress-band">
                            {snap.band}
                          </div>
                        )}
                        {snap?.exercise_rec_bg ? (
                          <div className={`mt-2 p-3 rounded-lg ${isDark ? "bg-orange-900/20" : "bg-orange-50"}`}
                            data-testid="stress-exercise-rec">
                            <div className="text-[10px] uppercase opacity-50 mb-1">
                              Препоръка за днешната тренировка
                            </div>
                            <p className="text-sm font-semibold">{snap.exercise_rec_bg}</p>
                          </div>
                        ) : (
                          <p className="text-xs opacity-60 mt-2">
                            Запишете днешния стрес по-долу за персонализирана препоръка.
                          </p>
                        )}
                        {snap?.sub_scores && (
                          <div className="grid grid-cols-3 gap-2 mt-3">
                            {[
                              { id: "food_cortisol", label: "Храна" },
                              { id: "sleep_stress", label: "Сън" },
                              { id: "perceived", label: "Усещане" },
                            ].map((s) => (
                              <div key={s.id}
                                className={`p-2 rounded-md text-center ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`}
                                data-testid={`stress-sub-${s.id}`}>
                                <div className="text-[9px] uppercase opacity-60">{s.label}</div>
                                <div className="text-lg font-bold">{snap.sub_scores[s.id] ?? "—"}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Section>

                <Section icon={Save} title="Запиши днешния стрес"
                  help="Една оценка на ден е достатъчна, за да се движи кривата."
                  testid="stress-section-log">
                  <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="stress-form">
                    <input type="range" min="1" max="10" value={perceived}
                      onChange={(e) => setPerceived(e.target.value)} className="w-full"
                      data-testid="stress-perceived-slider" />
                    <div className="flex justify-between text-[10px] opacity-60 mt-1">
                      <span>1 — спокойно</span>
                      <span className="font-bold text-base text-orange-500">{perceived}/10</span>
                      <span>10 — много стресирано</span>
                    </div>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="Бележка (по желание) — какво Ви причини стрес днес?"
                      className={`w-full mt-3 px-3 py-2 rounded-md border text-sm ${
                        isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`}
                      rows="2" data-testid="stress-note" />
                    <Button onClick={save} disabled={saving}
                      className="w-full mt-3 bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="stress-save-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              : <Save className="w-4 h-4 mr-2" />}
                      Запиши
                    </Button>
                  </div>
                </Section>

                <Section icon={TrendingDown} title="Кортизолов тренд и дневник на симптомите"
                  help="Слят със стария Cortisol Tracker — един дневник за симптомите."
                  testid="stress-section-trend">
                  <CortisolTrendCard isDark={isDark} />
                </Section>

                {insight && (                  <Section icon={Sparkles} title="AI препоръка"
                    help="Кратък разбор на данните Ви — образователен, не е лекарско назначение."
                    testid="stress-section-ai">
                    <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="stress-insight">
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{insight}</p>
                    </div>
                  </Section>
                )}

                <Section icon={Utensils} title="Релаксираща чиния"
                  help="Готова чиния срещу кортизола — записва се в дневника с едно докосване."
                  testid="stress-section-relax-plate">
                  <RelaxationPlateCard isDark={isDark} />
                </Section>

                {axis && (
                  <Section icon={Activity} title="Ос кортизол — щитовидна жлеза"
                    help="Засечена връзка между стреса и щитовидните Ви показатели."
                    testid="stress-section-axis">
                    <CortisolThyroidAxisCard axis={axis} defaultCollapsed={false} />
                  </Section>
                )}
              </>
            )}

            {tab === "intel" && (
              <Section icon={Brain} title={moduleTitle("cortisol")}
                help="Симптомен профил, дневник на ритъма, разчитане на кривата и интервенции."
                testid="stress-section-intel">
                <CortisolIntelCard isDark={isDark} />
              </Section>
            )}

            {tab === "program" && (
              <Section icon={CalendarCheck} title="28-дневна програма"
                help="Седмични навици и меню, вечерен ритуал, дишане и отчет „преди и сега“."
                testid="stress-section-program">
                <CortisolProgramCard isDark={isDark} />
              </Section>
            )}

            <Section icon={Activity} title="Физиологичен мост"
              help="Обем и осмолалитет, електролити и възбудимост, мембранен транспорт и хомеостазни контури — общият език с останалите близнаци."
              testid="stress-section-physiology">
              <PhysiologyBridgeCard focus="cortisol" isDark={isDark} />
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
